import React, { useState } from 'react';
import { X, Plus, Send, Users, User as UserIcon, Check, Eye, EyeOff } from 'lucide-react';
import { CATEGORIES, REPEAT_TYPES, WEEKDAYS, REWARD_EMOJIS } from '../lib/theme.js';
import { todayStr, addDays } from '../lib/dates.js';

export function AddTaskModal({ theme, mode, partner, onClose, onSubmit, editTask }) {
  const isEdit = !!editTask;
  const initialList = editTask?.list || (mode === 'send' ? 'sent' : mode === 'shared' ? 'shared' : 'private');

  const [title, setTitle] = useState(editTask?.title || '');
  const [categoryId, setCategoryId] = useState(editTask?.categoryId || 'daily');
  const [list, setList] = useState(initialList);
  const [tagPoints, setTagPoints] = useState(editTask?.tagPoints || 5);
  const [message, setMessage] = useState(editTask?.message || '');
  const [messageHidden, setMessageHidden] = useState(editTask?.messageHidden || false);
  const [reward, setReward] = useState(editTask?.reward || '');
  const [rewardEmoji, setRewardEmoji] = useState(editTask?.rewardEmoji || '🎁');
  const [rewardHidden, setRewardHidden] = useState(editTask?.rewardHidden ?? true);
  const [showRewardPicker, setShowRewardPicker] = useState(false);
  const [hasDeadline, setHasDeadline] = useState(editTask ? !!editTask.deadline : (initialList === 'sent'));
  const [deadline, setDeadline] = useState(editTask?.deadline ? editTask.deadline.split('T')[0] : addDays(todayStr(), 3));
  const [repeatType, setRepeatType] = useState(editTask?.repeatType || 'none');
  const [interval, setInterval] = useState(editTask?.interval || 3);
  const [monthDay, setMonthDay] = useState(editTask?.monthDay || 1);
  // Multi-weekday support: an array of weekday numbers (0=Sunday ... 6=Saturday).
  // Backward compat: if editing a task with single `weekday`, wrap in array.
  const initialWeekdays = (() => {
    if (Array.isArray(editTask?.weekdays) && editTask.weekdays.length > 0) {
      return [...editTask.weekdays].map(Number);
    }
    if (Array.isArray(editTask?.repeatWeekdays) && editTask.repeatWeekdays.length > 0) {
      return [...editTask.repeatWeekdays].map(Number);
    }
    if (editTask?.weekday !== undefined && editTask.weekday !== null) {
      return [Number(editTask.weekday)];
    }
    return [1]; // Monday default
  })();
  const [weekdays, setWeekdays] = useState(initialWeekdays);
  const [submitting, setSubmitting] = useState(false);

  function toggleWeekday(id) {
    setWeekdays(prev => {
      if (prev.includes(id)) {
        // Don't allow deselecting the last one
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== id);
      }
      return [...prev, id].sort((a, b) => a - b);
    });
  }

  async function handleSubmit() {
    if (!title.trim() || submitting) return;
    setSubmitting(true);

    try {
      const data = {
        title: title.trim(),
        categoryId, list, repeatType,
        deadline: hasDeadline ? `${deadline}T20:00:00` : null,
      };
      if (list === 'sent' && partner) {
        data.assignedTo = partner.id;
        data.tagPoints = parseInt(tagPoints) || 5;
        data.message = message.trim() || null;
        data.messageHidden = message.trim() ? messageHidden : false;
        data.reward = reward.trim() || null;
        data.rewardEmoji = reward.trim() ? rewardEmoji : null;
        data.rewardHidden = reward.trim() ? rewardHidden : false;
      }
      if (repeatType === 'every_n_days' || repeatType === 'every_n_weeks') data.interval = parseInt(interval) || 1;
      if (repeatType === 'monthly_date') data.monthDay = parseInt(monthDay) || 1;
      if (repeatType === 'weekday') {
        data.weekdays = [...weekdays].sort((a, b) => a - b);
        data.weekday = weekdays[0]; // backward-compat for single column
      }

      await onSubmit(data, editTask?.id);
      onClose();
    } catch (e) {
      alert('Der opstod en fejl: ' + e.message);
      setSubmitting(false);
    }
  }

  const titles = isEdit
    ? { private: 'Rediger privat opgave', sent: `Rediger opgave til ${partner?.name || 'partner'}`, shared: 'Rediger fælles opgave' }
    : { private: 'Ny privat opgave', sent: `Ny opgave til ${partner?.name || 'partner'}`, shared: 'Ny fælles opgave' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 200, padding: 0,
    }} onClick={onClose}>
      <div style={{
        background: theme.bgElevated,
        borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 560,
        maxHeight: '92vh',
        overflowY: 'auto',
        animation: 'tt-slide-up 0.3s ease-out',
      }} onClick={e => e.stopPropagation()}>
        <style>{`@keyframes tt-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky', top: 0,
          background: theme.bgElevated,
          zIndex: 1,
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{titles[list]}</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: theme.textMuted,
            cursor: 'pointer', padding: 4,
          }}><X size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Type selector */}
          {!isEdit && partner && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {[
                { id: 'private', label: 'Privat', color: theme.textMuted, icon: UserIcon },
                { id: 'sent', label: `Til ${partner.name}`, color: theme.coral, icon: Send },
                { id: 'shared', label: 'Fælles', color: theme.teal, icon: Users },
              ].map(t => {
                const Icon = t.icon;
                const active = list === t.id;
                return (
                  <button key={t.id} onClick={() => setList(t.id)} style={{
                    border: `1px solid ${active ? t.color : theme.border}`,
                    background: active ? `${t.color}20` : theme.bg,
                    color: active ? t.color : theme.textMuted,
                    padding: '10px 6px', borderRadius: 10,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    <Icon size={14} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Title */}
          <Field theme={theme} label="Titel">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder={list === 'sent' ? 'F.eks. Mal plankeværket' : 'Hvad skal der gøres?'}
              style={inputStyle(theme)} autoFocus />
          </Field>

          {/* Sent-only: points */}
          {list === 'sent' && (
            <Field theme={theme} label={`Pointværdi: ${tagPoints}`}>
              <input type="range" min="1" max="50" value={tagPoints} onChange={e => setTagPoints(e.target.value)}
                style={{ width: '100%', accentColor: theme.coral }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: theme.textDim, marginTop: 4 }}>
                <span>Hurtigt</span><span>Stor opgave</span>
              </div>
            </Field>
          )}

          {/* Sent-only: reward */}
          {list === 'sent' && (
            <Field theme={theme} label="Belønning (valgfri)">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <button onClick={() => setShowRewardPicker(v => !v)} style={{
                  background: theme.bg, border: `1px solid ${theme.border}`,
                  padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 18,
                }}>{rewardEmoji}</button>
                <input type="text" value={reward} onChange={e => setReward(e.target.value)}
                  placeholder="F.eks. En is, massage, god middag"
                  style={{ ...inputStyle(theme), flex: 1 }} />
              </div>
              {showRewardPicker && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginBottom: 6 }}>
                  {REWARD_EMOJIS.map(r => (
                    <button key={r.emoji}
                      onClick={() => { setRewardEmoji(r.emoji); if (!reward) setReward(r.label); setShowRewardPicker(false); }}
                      style={{
                        background: theme.bg, border: `1px solid ${theme.border}`,
                        padding: '6px', borderRadius: 6, cursor: 'pointer', fontSize: 16,
                      }}>{r.emoji}</button>
                  ))}
                </div>
              )}
              {reward && (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <Toggle theme={theme} active={!rewardHidden} onClick={() => setRewardHidden(false)}
                    icon={<Eye size={11} />} label="Vis som motivation" />
                  <Toggle theme={theme} active={rewardHidden} onClick={() => setRewardHidden(true)}
                    icon={<EyeOff size={11} />} label="Skjul som overraskelse" />
                </div>
              )}
            </Field>
          )}

          {/* Sent-only: message */}
          {list === 'sent' && (
            <Field theme={theme} label="Lille besked (valgfri)">
              <input type="text" value={message} onChange={e => setMessage(e.target.value)}
                placeholder='F.eks. "Tak skat 💕"'
                style={inputStyle(theme)} />
              {message && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: theme.textMuted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={messageHidden} onChange={e => setMessageHidden(e.target.checked)}
                    style={{ accentColor: theme.coral }} />
                  Skjul beskeden — afsløres ved løsning
                </label>
              )}
            </Field>
          )}

          {/* Category */}
          <Field theme={theme} label="Kategori">
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={inputStyle(theme)}>
              {CATEGORIES.map(c => <option key={c.id} value={c.id} style={{ background: theme.bg }}>{c.emoji} {c.name}</option>)}
            </select>
          </Field>

          {/* Deadline */}
          <Field theme={theme} label="Deadline">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.textMuted, marginBottom: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={hasDeadline} onChange={e => setHasDeadline(e.target.checked)} style={{ accentColor: theme.coral }} />
              Brug deadline
            </label>
            {hasDeadline && (
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
                style={{ ...inputStyle(theme), colorScheme: theme.isDark ? 'dark' : 'light' }} />
            )}
          </Field>

          {/* Repeat */}
          <Field theme={theme} label="Gentagelse">
            <select value={repeatType} onChange={e => setRepeatType(e.target.value)} style={inputStyle(theme)}>
              {REPEAT_TYPES.map(r => <option key={r.id} value={r.id} style={{ background: theme.bg }}>{r.name}</option>)}
            </select>
            {(repeatType === 'every_n_days' || repeatType === 'every_n_weeks') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color: theme.textMuted }}>
                <span>Hver</span>
                <input type="number" min="1" max="60" value={interval} onChange={e => setInterval(e.target.value)}
                  style={{ ...inputStyle(theme), width: 70 }} />
                <span>{repeatType === 'every_n_days' ? 'dag' : 'uge'}</span>
              </div>
            )}
            {repeatType === 'monthly_date' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color: theme.textMuted }}>
                <span>Den</span>
                <input type="number" min="1" max="31" value={monthDay} onChange={e => setMonthDay(e.target.value)}
                  style={{ ...inputStyle(theme), width: 70 }} />
                <span>i hver måned</span>
              </div>
            )}
            {repeatType === 'weekday' && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>
                  Vælg ugedage {weekdays.length > 1 && `(${weekdays.length} valgt)`}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                  {WEEKDAYS.map(w => {
                    const active = weekdays.includes(w.id);
                    return (
                      <button key={w.id} type="button" onClick={() => toggleWeekday(w.id)}
                        style={{
                          border: `1px solid ${active ? theme.coral : theme.border}`,
                          background: active ? `${theme.coral}15` : theme.bg,
                          color: active ? theme.coral : theme.textMuted,
                          padding: '8px 4px', borderRadius: 8,
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        }}
                      >{w.short}</button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button type="button" onClick={() => setWeekdays([1, 2, 3, 4, 5])} style={quickPickBtn(theme)}>
                    Hverdage
                  </button>
                  <button type="button" onClick={() => setWeekdays([0, 6])} style={quickPickBtn(theme)}>
                    Weekend
                  </button>
                  <button type="button" onClick={() => setWeekdays([0, 1, 2, 3, 4, 5, 6])} style={quickPickBtn(theme)}>
                    Alle dage
                  </button>
                </div>
              </div>
            )}
          </Field>
        </div>

        {/* Footer */}
        <div style={{
          padding: 20, borderTop: `1px solid ${theme.border}`,
          display: 'flex', gap: 8,
          position: 'sticky', bottom: 0, background: theme.bgElevated,
        }}>
          <button onClick={onClose} style={{ ...btn(theme, 'ghost'), flex: 1 }}>Annuller</button>
          <button onClick={handleSubmit} disabled={!title.trim() || submitting} style={{ ...btn(theme, 'primary'), flex: 2, justifyContent: 'center' }}>
            {submitting ? 'Gemmer...' : isEdit ? <><Check size={14} /> Gem ændringer</> : list === 'sent' ? <><Send size={14} /> Send til {partner?.name}</> : <><Plus size={14} /> Tilføj</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ theme, label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ theme, active, onClick, icon, label }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '8px 10px', borderRadius: 8,
      border: `1px solid ${active ? theme.coral : theme.border}`,
      background: active ? `${theme.coral}20` : theme.bg,
      color: active ? theme.coral : theme.textMuted,
      fontSize: 11, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    }}>
      {icon} {label}
    </button>
  );
}

function inputStyle(theme) {
  return {
    width: '100%',
    padding: '10px 12px',
    background: theme.bg,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    color: theme.text,
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
  };
}

function btn(theme, variant) {
  const base = {
    padding: '12px 16px', borderRadius: 10,
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    border: 'none',
    display: 'flex', alignItems: 'center', gap: 6,
  };
  if (variant === 'primary') return { ...base, background: theme.coral, color: 'white' };
  if (variant === 'ghost') return { ...base, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}` };
  return base;
}

function quickPickBtn(theme) {
  return {
    flex: 1,
    padding: '6px 8px',
    borderRadius: 7,
    border: `1px dashed ${theme.border}`,
    background: 'transparent',
    color: theme.textMuted,
    fontSize: 10,
    fontWeight: 600,
    cursor: 'pointer',
  };
}
